import { Component, type ErrorInfo, type ReactNode } from 'react'

/**
 * The signed-in session lives in React state, so ANY unhandled render error unmounts the
 * app and silently signs the organizer out mid-task. That is how clearing a date field
 * appeared to log people out: a formatter threw on an empty value and took the session
 * with it.
 *
 * This does not hide bugs - it keeps a render fault from destroying work in progress and
 * from being misread as an authentication problem. The error is still reported to the
 * console for diagnosis.
 */
type Props = { children: ReactNode }
type State = { error: Error | null }

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('EtixHub render error', error, info.componentStack)
  }

  render() {
    if (!this.state.error) return this.props.children
    return <main className="sf-empty" role="alert">
      <span className="sf-wordmark">EtixHub</span>
      <p className="sf-kicker">This page could not load</p>
      <h1>You are still signed in.</h1>
      <p>Your work has not been submitted. Return to the previous screen and try again; if it keeps happening, share the diagnostic below.</p>
      <p><code>{this.state.error.message}</code></p>
      <button className="button primary" type="button" onClick={() => this.setState({ error: null })}>Try this screen again</button>
    </main>
  }
}
